/**
 * AppRuntime — pure TypeScript runtime for Cogo v2 apps.
 *
 * Zero React imports. All state management, flow execution, event dispatch,
 * page tree building, and navigation logic lives here.
 * React shell (AppShell) subscribes to state changes and re-renders.
 *
 * Block-Centric Lifecycle:
 * - Runtime is created ONCE per app (not per blocks change).
 * - Blocks are added incrementally via BlockLifecycleManager.
 * - Init flows execute in 2 phases: layout (await) → content (parallel).
 * - Generation counter protects against StrictMode double-mount.
 */

import { ElementRegistry, registerAllRenderers, normalizeNavLayout } from "@cogo/react-renderer"
import type { NavLayoutUiNode } from "@cogo/react-renderer"
import type { App, BlockDef, UiElement, EventRef, Flow, FlowMap, Page, NavigationData, DataSource } from "@cogo/types-v2"
import { isAbsolutePosition } from "@cogo/types-v2"
import { StateManager, ActionFlowEngine, BlockLifecycleManager, resolveNavFromRoute } from "@cogo/runtime-v2"
import type { PlatformAdapter, ToastHandler } from "@cogo/runtime-v2"
import type { SandboxModules } from '../sandbox/index.js'
import { EventDispatcherV2 } from "./event-dispatcher"

// Phase 152: Sandbox-aware platform adapter extension.
// Phase 153 will provide this when constructing the adapter for the Page Editor.
// basePlatform is typed as PlatformAdapter but may carry sandbox modules at runtime.
interface SandboxAwarePlatform extends PlatformAdapter {
  sandbox?: Partial<SandboxModules>
}
import {
  createWebMCPIntegration,
  type WebMCPIntegration,
  type JsonSchema,
  generateToolsFromApp,
  generatePageContextTools,
  generateVisibleElementsTools,
  formatToolError,
} from "@cogo/webmcp-runtime"

// ─── Helpers ──────────────────────────────────────────────────────────────

/**
 * Walk each block's ui root and apply nav-layout normalization. Mutates in
 * place — callers pass the blocks record before lifecycle.addBlocks so the
 * state manager / event bindings see the rewritten tree.
 *
 * No-op on blocks missing a ui root or non-object ui trees. Compliant
 * classNames are unchanged; only violations are rewritten.
 */
function applyNavLayoutNormalization(blocks: Record<string, BlockDef>): void {
  for (const block of Object.values(blocks)) {
    const root = block?.ui?.root
    if (root && typeof root === "object") {
      normalizeNavLayout(root as unknown as NavLayoutUiNode)
    }
  }
}

// ─── Public interface ─────────────────────────────────────────────────────

export interface AppRuntime {
  stateManager: StateManager
  actionEngine: ActionFlowEngine
  eventDispatcher: EventDispatcherV2
  lifecycle: BlockLifecycleManager
  registry: ElementRegistry
  currentRoute: string

  navigate(route: string): void
  resolveSymbol(expr: string): unknown
  dispatchEvent(elementId: string, eventType: string, payload?: unknown): void
  getCurrentPage(): Page
  buildPageTree(): UiElement[]
  subscribe(listener: () => void): () => void
  runInitFlows(options?: { isRouteChange?: boolean }): Promise<void>
  /** @deprecated Use addBlocks() instead */
  updateBlocks(blocks: Record<string, BlockDef>): void
  addBlocks(blocks: Record<string, BlockDef>): string[]
  registerAllFlows(): void
  nextGeneration(): number
  destroy(): void
}

export interface ThemeTokens {
  colors?: Record<string, string>
  radius?: string
  /**
   * Font stacks (Phase 393) — emitted as --font-sans / --font-mono /
   * --font-heading CSS variables when passed through getIsolatedAppStyle().
   * Parity with v1 CogoBlock ThemeTokens.
   */
  fonts?: { sans?: string; mono?: string; heading?: string }
}

export interface AppRuntimeOptions {
  platform?: PlatformAdapter
  onToast?: ToastHandler
  initialRoute?: string
  onNavigate?: (route: string) => void | Promise<void>
  designSystem?: Record<string, string>
  /** Theme tokens (colors + radius) — injected into :root CSS variables at runtime */
  themeTokens?: ThemeTokens
}

// ─── Singleton registry ───────────────────────────────────────────────────

let sharedRegistry: ElementRegistry | null = null
export function getSharedRegistry(): ElementRegistry {
  if (!sharedRegistry) {
    sharedRegistry = new ElementRegistry()
    registerAllRenderers(sharedRegistry)
  }
  return sharedRegistry
}

// ─── Factory ──────────────────────────────────────────────────────────────

export function createAppRuntime(
  manifest: App,
  blocks: Record<string, BlockDef>,
  options: AppRuntimeOptions = {},
): AppRuntime {
  return new AppRuntimeImpl(manifest, blocks, options)
}

// ─── Implementation ───────────────────────────────────────────────────────

class AppRuntimeImpl implements AppRuntime {
  readonly stateManager: StateManager
  readonly actionEngine: ActionFlowEngine
  readonly eventDispatcher: EventDispatcherV2
  readonly lifecycle: BlockLifecycleManager
  readonly registry: ElementRegistry

  private _currentRoute: string
  private readonly manifest: App
  private readonly options: AppRuntimeOptions

  private readonly designSystem: Record<string, string>
  private readonly computedExprs = new Map<string, string>()
  private readonly flowTimestamps = new Map<string, number>()
  private readonly activeBlocks = new Set<string>()
  private lastNavigate: { route: string; time: number } | null = null
  private readonly listeners = new Set<() => void>()
  private unsubState: (() => void) | null = null
  private webmcp: WebMCPIntegration | null = null

  /** Merchant context extracted from JWT for auto-injection (D-09) */
  private merchantId: string | null = null

  /** Convenience getter — delegates to lifecycle manager */
  private get blocks(): Record<string, BlockDef> {
    return this.lifecycle.getAllBlocks()
  }

  constructor(manifest: App, blocks: Record<string, BlockDef>, options: AppRuntimeOptions) {
    this.manifest = manifest
    this.options = options
    this.designSystem = options.designSystem ?? {}
    this.registry = getSharedRegistry()

    const defaultRoute = manifest.navigation?.defaultRoute ?? manifest.pages[0]?.route ?? "/"
    this._currentRoute = options.initialRoute ?? defaultRoute

    // 1. StateManager — permissive mode
    const emptyContract = { localState: {}, inputSymbols: [], outputSymbols: [] }
    this.stateManager = new StateManager({ state: manifest.globalState ?? {} }, emptyContract, { permissive: true })

    // 2. ActionFlowEngine — full PlatformAdapter (v18.0: all 27+ methods)
    // Cast to SandboxAwarePlatform so sandbox modules can be accessed via basePlatform.sandbox.*
    // Phase 153 will provide a platform adapter that carries sandbox modules in edit mode.
    const basePlatform = options.platform as SandboxAwarePlatform | undefined
    const platformAdapter: PlatformAdapter = {
      // Navigation — override push/replace to use internal routing
      pushRoute: (route: string) => { this.setRoute(route) },
      replaceRoute: (route: string) => { this.setRoute(route) },
      goBack: () => {
        if (basePlatform?.sandbox?.navigation) {
          basePlatform.sandbox.navigation.history.back()
          return
        }
        if (basePlatform?.goBack) basePlatform.goBack()
        else if (typeof window !== "undefined") window.history.back()
      },
      openExternal: (url, newTab) => {
        // Sandbox intercept: in edit mode, openExternal with newTab===false is sandboxed
        if (basePlatform?.sandbox?.navigation && newTab === false) {
          basePlatform.sandbox.navigation.openExternal(url)
          return
        }
        if (basePlatform?.openExternal) { basePlatform.openExternal(url, newTab); return }
        // Web fallback when no platform adapter provided
        if (typeof window !== "undefined" && url) {
          if (newTab !== false) {
            // window.open may be blocked by popup blocker in async contexts — fallback to same-tab
            const win = window.open(url, "_blank", "noopener,noreferrer")
            if (!win) window.location.href = url
          } else { window.location.href = url }
        }
      },
      getCurrentPath: () => this._currentRoute,
      // Storage — sandbox-aware with fallback to localStorage
      getItem: (k) => basePlatform?.sandbox?.storage?.local.getItem(k) ?? basePlatform?.getItem?.(k) ?? (typeof localStorage !== "undefined" ? localStorage.getItem(k) : null),
      setItem: (k, v) => {
        if (basePlatform?.sandbox?.storage) { basePlatform.sandbox.storage.local.setItem(k, v); return }
        if (basePlatform?.setItem) basePlatform.setItem(k, v)
        else if (typeof localStorage !== "undefined") localStorage.setItem(k, v)
      },
      removeItem: (k) => {
        if (basePlatform?.sandbox?.storage) { basePlatform.sandbox.storage.local.removeItem(k); return }
        if (basePlatform?.removeItem) basePlatform.removeItem(k)
        else if (typeof localStorage !== "undefined") localStorage.removeItem(k)
      },
      clearStorage: () => {
        if (basePlatform?.sandbox?.storage) { basePlatform.sandbox.storage.local.clear(); return }
        if (basePlatform?.clearStorage) basePlatform.clearStorage()
        else if (typeof localStorage !== "undefined") localStorage.clear()
      },
      copyToClipboard: (t) => basePlatform?.copyToClipboard?.(t) ?? (typeof navigator !== "undefined" ? navigator.clipboard.writeText(t) : Promise.resolve()),
      readClipboard: () => basePlatform?.readClipboard?.() ?? (typeof navigator !== "undefined" ? navigator.clipboard.readText() : Promise.resolve("")),
      showToast: (p) => basePlatform?.showToast?.(p),
      showPopup: (p) => basePlatform?.showPopup?.(p),
      showLoading: (s) => basePlatform?.showLoading?.(s),
      scrollToElement: (id, s) => { if (basePlatform?.scrollToElement) basePlatform.scrollToElement(id, s); else document.getElementById(id)?.scrollIntoView(s ? { behavior: "smooth" } : undefined) },
      scrollToPosition: (y, s) => { if (basePlatform?.scrollToPosition) basePlatform.scrollToPosition(y, s); else window.scrollTo({ top: y, behavior: s ? "smooth" : "auto" }) },
      getScrollY: () => basePlatform?.getScrollY?.() ?? (typeof window !== "undefined" ? window.scrollY : 0),
      print: () => {
        if (basePlatform?.sandbox?.navigation) { basePlatform.sandbox.navigation.print(); return }
        if (basePlatform?.print) basePlatform.print()
        else if (typeof window !== "undefined") window.print()
      },
      share: (d) => basePlatform?.share?.(d) ?? Promise.resolve(),
      getGeolocation: () => basePlatform?.getGeolocation?.() ?? Promise.resolve({ latitude: 0, longitude: 0 }),
      getUserMedia: (c) => basePlatform?.getUserMedia?.(c) ?? Promise.resolve(null),
      download: (u, f) => basePlatform?.download?.(u, f),
      pickFile: (a) => basePlatform?.pickFile?.(a) ?? Promise.resolve(null),
      showNotification: (t, b) => basePlatform?.showNotification?.(t, b),
      digest: (a, d) => basePlatform?.digest?.(a, d) ?? Promise.resolve(""),
      // Network — sandbox-aware: use sandbox.network.fetch in edit mode to intercept credentials and relative URLs
      fetch: (u, o) => {
        if (basePlatform?.sandbox?.network) return basePlatform.sandbox.network.fetch(u, o)
        return basePlatform?.fetch?.(u, o) ?? globalThis.fetch(u, o)
      },
      createEventSource: (u, cb) => basePlatform?.createEventSource?.(u, cb) ?? { close() {} },
      createWebSocket: (u) => basePlatform?.createWebSocket?.(u) ?? { send() {}, close() {}, onMessage() {}, onClose() {} },
      dispatchAnalyticsEvent: (e, d) => basePlatform?.dispatchAnalyticsEvent?.(e, d),
      // v18.0 extension
      openAuthSession: basePlatform?.openAuthSession ? (u, r) => basePlatform.openAuthSession!(u, r) : undefined,
    }
    this.actionEngine = new ActionFlowEngine(this.stateManager, platformAdapter)
    if (options.onToast) this.actionEngine.onToast = options.onToast

    // 3. EventDispatcher
    this.eventDispatcher = new EventDispatcherV2()

    // 4. BlockLifecycleManager — wired to this runtime's init/flow/binding methods
    this.lifecycle = new BlockLifecycleManager({
      initBlockState: (name, block) => this.initSingleBlockState(name, block),
      registerBlockFlows: (name, block) => this.registerSingleBlockFlows(name, block),
      registerBlockBindings: (name, block) => {
        const rawNs = block.meta?.namespace ?? name
        const ns = camelToKebab(rawNs)
        this.registerBlockBindings(block, ns)
      },
      executeFlow: (flow, ctx) => this.actionEngine.executeFlow(flow, ctx),
      findFlow: (name) => this.actionEngine.findFlow(name),
      getRegisteredFlowNames: () => this.actionEngine.getRegisteredFlowNames(),
    })

    // ─── Initialize manifest-level state ─────────────────────────────
    this.initializeManifestState()
    this.registerBuiltinActions(platformAdapter)

    // ─── Normalize nav layout (Layer-3 contract enforcement) ──
    // Rewrites `flex items-center justify-between` → grid-cols-[auto_1fr_auto]
    // on nav 3-slot rows and strips w-full from logo containers inside nav
    // ancestors, matching validate.cjs rules 1/2. Safe-net for templates
    // whose source ui.json has not yet been migrated. No-op on compliant
    // trees. See lib/normalize-nav-layout.ts in cogo-react-renderer.
    applyNavLayoutNormalization(blocks)

    // ─── Add initial blocks via lifecycle (state + flows + bindings) ──
    this.lifecycle.addBlocks(blocks)

    // State subscription — re-eval computed on every change
    this.unsubState = this.stateManager.subscribe("", () => {
      for (const [key, expr] of this.computedExprs) {
        const resolved = this.stateManager.resolve(expr)
        this.stateManager.setStateQuiet(`computed.${key}`, resolved)
      }
      this.notifyListeners()
    })

    // D-09: Extract merchantId from JWT for auto-injection into all fetch calls
    this.setMerchantContext()
  }

  get currentRoute(): string { return this._currentRoute }

  // ─── Merchant Context (D-09) ────────────────────────────────────

  /** Extract merchantId from JWT stored in partner-auth-token cookie */
  private setMerchantContext(): void {
    try {
      const cookieStr = typeof document !== 'undefined' ? (document.cookie || '') : ''
      const match = cookieStr.match(/partner-auth-token=([^;]+)/)
      if (match) {
        const payload = JSON.parse(atob(match[1].split('.')[1]))
        this.merchantId = payload.app_metadata?.merchant_id || payload.merchantId || null
      }
    } catch {
      // No merchant context available (e.g., SSR or non-merchant user)
    }
  }

  /** Build merchant headers for auto-injection into all fetch calls */
  private getMerchantHeaders(): Record<string, string> {
    if (!this.merchantId) return {}
    return { 'X-Merchant-Id': this.merchantId }
  }

  // ─── Public API ──────────────────────────────────────────────────

  navigate(route: string): void {
    // __action: prefix — dispatch flow instead of navigating
    if (route.startsWith("__action:")) {
      const flowName = route.slice("__action:".length)
      const flow = this.actionEngine.findFlow(flowName)
      if (flow) {
        this.actionEngine.executeFlow(flow, {}).then(() => this.notifyListeners())
      } else {
        console.warn(`[AppRuntime] __action flow not found: "${flowName}"`)
      }
      return
    }
    const now = Date.now()
    if (this.lastNavigate && this.lastNavigate.route === route && now - this.lastNavigate.time < 300) return
    this.lastNavigate = { route, time: now }
    this.setRoute(route).catch(() => { /* handled in setRoute / navigate_to handler */ })
  }

  resolveSymbol(expr: string): unknown {
    // Pre-resolve @{...} local references
    if (typeof expr === "string" && expr.includes("@{")) {
      const pureLocal = /^@\{([^}]+)\}$/.exec(expr)
      if (pureLocal) {
        const path = pureLocal[1].trim()
        if (path.startsWith("!")) return !this.stateManager.resolve(`#{${path.slice(1)}}`)
        return this.stateManager.resolve(`#{${path}}`)
      }
      expr = expr.replace(/@\{([^}]+)\}/g, (_, inner) => {
        const path = (inner as string).trim()
        if (path.startsWith("!")) {
          const val = this.stateManager.resolve(`#{${path.slice(1)}}`)
          return String(!val)
        }
        const val = this.stateManager.resolve(`#{${path}}`)
        return val != null ? String(val) : ""
      })
      if (!expr.includes("#{")) return expr
    }

    // Live re-evaluate computed expressions
    if (typeof expr === "string") {
      const computedMatch = /^#\{computed\.(\w+)\}$/.exec(expr)
      if (computedMatch) {
        const key = computedMatch[1]
        const originalExpr = this.computedExprs.get(key)
        if (originalExpr) {
          const resolved = this.stateManager.resolve(originalExpr)
          this.stateManager.setStateQuiet(`computed.${key}`, resolved)
          return resolved
        }
      }
    }

    return this.stateManager.resolve(expr) ?? ""
  }

  dispatchEvent(elementId: string, eventType: string, payload?: unknown): void {
    this.eventDispatcher.dispatch(elementId, eventType, payload)

    const payloadObj = (payload != null && typeof payload === "object")
      ? payload as Record<string, unknown>
      : {}

    const binding = this.eventDispatcher.getBinding(elementId, eventType, this.activeBlocks)
    const flowName = binding?.flow
    if (!flowName) {
      console.debug(`[AppRuntime] No binding for ${elementId}:${eventType}`,
        { activeBlocks: [...this.activeBlocks], totalBindings: Object.keys(this.eventDispatcher["bindingsByKey"]).length })
      return
    }

    // Built-in navigate action (with dedup guard)
    if (flowName.startsWith("__navigate:")) {
      const resolvedRoute = payloadObj.__resolvedRoute as string | undefined
      const route = resolvedRoute || flowName.slice("__navigate:".length)
      const now = Date.now()
      if (this.lastNavigate && this.lastNavigate.route === route && now - this.lastNavigate.time < 300) return
      this.lastNavigate = { route, time: now }
      this.setRoute(route)
      return
    }

    // Flow debounce for tap events (300ms)
    if (eventType === "tap") {
      const lockKey = `${elementId}:${eventType}`
      const now = Date.now()
      const lastExec = this.flowTimestamps.get(lockKey)
      if (lastExec && now - lastExec < 300) return
      this.flowTimestamps.set(lockKey, now)
    }

    // Resolve flow: block-scoped → exact → suffix match
    const blockNs = binding.__ns
    const resolvedFlow = (blockNs ? this.actionEngine.findFlow(`${blockNs}.${flowName}`) : undefined)
      ?? this.actionEngine.findFlow(flowName)
      ?? this.findFlowBySuffix(flowName)
    if (!resolvedFlow) {
      console.warn(`[AppRuntime] Flow not found: "${flowName}" (ns: ${blockNs})`)
      return
    }

    // Build flow context from binding.params + payload
    const { __resolvedRoute: _, ...itemContext } = payloadObj
    const rawEvent = (payload != null && typeof payload === "object")
      ? payload as Record<string, unknown>
      : { value: payload }

    const eventParams = binding.params
      ? { ...rawEvent, ...resolveBindingParams(binding.params, { ...itemContext, event: rawEvent }, this.stateManager) }
      : payload != null
        ? (typeof payload === "object" ? payload : { value: payload })
        : {}
    const flowContext: Record<string, unknown> = { event: eventParams, ...itemContext }

    this.actionEngine.executeFlow(resolvedFlow, flowContext).then(() => {
      this.notifyListeners()
    })
  }

  getCurrentPage(): Page {
    // Score matching pages by specificity: literal segments > parameterized segments.
    // Prevents catch-all routes like /admin/:a/:b/:c from shadowing more specific
    // routes like /admin/customers/form/:id? (both have 5 segments, both match, but
    // the latter has more literal segments and should win).
    const matches = this.manifest.pages
      .map((p, idx) => ({ p, idx, score: scorePageSpecificity(p.route, this._currentRoute) }))
      .filter((m) => m.score >= 0)
    if (matches.length === 0) return this.manifest.pages[0]
    // Highest score wins; tie-break by manifest order (first wins) for determinism.
    matches.sort((a, b) => (b.score - a.score) || (a.idx - b.idx))
    return matches[0].p
  }

  buildPageTree(): UiElement[] {
    const page = this.getCurrentPage()
    if (!page) return []
    // Ensure page.blocks is an array (may be undefined during generation)
    if (!Array.isArray(page.blocks)) page.blocks = []

    // ── Sidebar layout: page.layout === "sidebar" ────────────────
    if (page.layout === "sidebar") {
      return this.buildSidebarPageTree(page)
    }

    // ── Absolute layout: blocks positioned by x,y,w,h ────────────
    if (page.layout === "absolute") {
      return this.buildAbsolutePageTree(page)
    }

    // ── Custom layout: page.ui.root defines the tree ──────────────
    if ((page as any).ui?.root) {
      return [(page as any).ui.root]
    }

    // ── Layout-block wrapping: page.layout references a block name ──
    const layoutBlockName = page.layout !== "stack" && page.layout ? page.layout : undefined
    const layoutBlock = layoutBlockName ? this.blocks[layoutBlockName] : undefined

    if (layoutBlock?.ui?.root) {
      const enrichedLayout = this.enrichBlock(layoutBlockName!)
      const layoutUi = structuredClone(enrichedLayout?.ui ?? layoutBlock.ui)
      const contentSlot = findContentSlot(layoutUi.root!)
      if (contentSlot) {
        const cls = contentSlot.className ?? ""
        if (!cls.includes("flex-col")) {
          contentSlot.className = cls + (cls ? " " : "") + "flex flex-col"
        }

        contentSlot.children = page.blocks
          .filter((name) => {
            if (name === layoutBlockName) return false
            const block = this.blocks[name]
            return block && block.meta?.type !== "logic"
          })
          .map((name) => {
            const root = this.enrichBlock(name)?.ui?.root
            if (!root) return null
            const bl = page.blockLayout?.[name]
            const gapClass = bl?.gap ?? bl?.spacing ?? ""
            if (gapClass) {
              return { id: `section-${name}`, type: "container", className: gapClass, children: [root] } as UiElement
            }
            return root
          })
          .filter(Boolean) as UiElement[]
      }
      return [layoutUi.root!]
    }

    // ── Stack layout (default) ───────────────────────────────────
    const blockElements: UiElement[] = []
    for (const blockName of page.blocks) {
      const block = this.blocks[blockName]
      if (!block || block.meta?.type === "logic") continue
      const enriched = this.enrichBlock(blockName)
      const root = enriched?.ui?.root
      if (!root) continue

      // Skip stub blocks — blocks with only a single text child containing [block-name] placeholder
      if (isStubBlock(root, blockName)) continue

      const bl = page.blockLayout?.[blockName]

      if (isHeaderBlock(root)) {
        blockElements.push(root)
        continue
      }

      const widthClass = bl?.width ? (WIDTH_CLASS_MAP[bl.width] ?? "") : ""
      const gapClass = bl?.gap ?? bl?.spacing ?? ""
      const isFooter = isFooterBlock(blockName, root)
      const wrapperParts = ["overflow-x-hidden w-full", widthClass, gapClass]
      if (isFooter) wrapperParts.push("mt-auto")
      const wrapperClass = wrapperParts.filter(Boolean).join(" ")

      blockElements.push({
        id: `section-${blockName}`,
        type: "container",
        tag: isFooter ? "footer" : "section",
        className: wrapperClass,
        children: [root],
      })
    }

    return [{
      id: `page-${page.id}`,
      type: "container",
      className: "overflow-x-hidden max-w-[100vw] w-full flex flex-col",
      children: blockElements,
    }]
  }

  /**
   * Build sidebar layout tree.
   */
  private buildSidebarPageTree(page: Page): UiElement[] {
    const sidebarElements: UiElement[] = []
    const headerElements: UiElement[] = []
    const contentElements: UiElement[] = []

    for (const blockName of page.blocks) {
      const block = this.blocks[blockName]
      if (!block || block.meta?.type === "logic") continue
      const enriched = this.enrichBlock(blockName)
      const root = enriched?.ui?.root
      if (!root) continue

      const isSidebar = root.type === "aside" || root.tag === "aside" || root.variant === "sidebar"
      const isHeader = root.variant === "header" || root.tag === "header"
        || root.type === "nav" || blockName.includes("topnav")
        || root.position?.type === "sticky"

      if (isSidebar) {
        sidebarElements.push(root)
      } else if (isHeader) {
        headerElements.push(root)
      } else {
        const bl = page.blockLayout?.[blockName]
        const gapClass = bl?.gap ?? bl?.spacing ?? ""
        if (gapClass) {
          contentElements.push({
            id: `section-${blockName}`, type: "container", className: gapClass, children: [root],
          } as UiElement)
        } else {
          contentElements.push(root)
        }
      }
    }

    const mainArea: UiElement = {
      id: `page-${page.id}-main`,
      type: "container",
      className: "flex flex-1 flex-col overflow-hidden",
      children: [
        ...headerElements,
        {
          id: `page-${page.id}-content`,
          type: "container",
          tag: "main" as string,
          className: "flex-1 overflow-y-auto p-6",
          children: contentElements,
        } as UiElement,
      ],
    }

    const sidebarWrapper: UiElement = {
      id: `page-${page.id}-sidebar`,
      type: "container",
      tag: "aside" as string,
      className: "hidden shrink-0 overflow-y-auto md:block",
      children: sidebarElements,
    }

    return [{
      id: `page-${page.id}`,
      type: "container",
      className: "flex h-screen overflow-hidden",
      children: [
        ...(sidebarElements.length > 0 ? [sidebarWrapper] : []),
        mainArea,
      ],
    }]
  }

  /**
   * Build absolute-positioned page tree.
   * Each block is wrapped in a position:absolute container using page.blockPositions.
   * Canvas size from page.canvas (defaults to 1920x1080).
   */
  private buildAbsolutePageTree(page: Page): UiElement[] {
    const canvasW = page.canvas?.w ?? 1920
    const canvasH = page.canvas?.h ?? 1080
    const positions = page.blockPositions ?? {}
    const blockNames = page.blocks?.length
      ? page.blocks.filter(name => name in positions)
      : Object.keys(positions)

    const blockElements: UiElement[] = []

    for (let i = 0; i < blockNames.length; i++) {
      const name = blockNames[i]
      const pos = positions[name]
      if (!pos || !isAbsolutePosition(pos)) continue

      const enriched = this.enrichBlock(name)
      const root = enriched?.ui?.root
      if (!root) continue

      blockElements.push({
        id: `section-${name}`,
        type: "container",
        style: {
          position: "absolute",
          left: pos.x,
          top: pos.y,
          width: pos.w,
          height: pos.h,
          overflow: "hidden",
          zIndex: i,
        },
        children: [root],
      } as UiElement)
    }

    return [{
      id: `page-${page.id}`,
      type: "container",
      style: {
        position: "relative",
        width: canvasW,
        height: canvasH,
      },
      children: blockElements,
    } as UiElement]
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => { this.listeners.delete(listener) }
  }

  /**
   * Run init flows for the current page in 2 phases via BlockLifecycleManager.
   *
   * The synchronous part (state re-apply, flow registration, activeBlocks) runs inline.
   * Init flow execution is fire-and-forget (same as original behavior) — the returned
   * Promise can be awaited by callers who need to wait for init flows to complete
   * (e.g., App.tsx on first mount).
   *
   * Generation counter protects against stale results from StrictMode double-mount.
   */
  runInitFlows(options?: { isRouteChange?: boolean }): Promise<void> {
    // (Re-)install WebMCP polyfill — must run here (not constructor) to survive
    // StrictMode destroy/re-mount cycle. Idempotent: dispose previous, install fresh.
    this.installWebMCP()

    const page = this.getCurrentPage()
    if (!page) return Promise.resolve()

    // ── Synchronous setup (must complete inline for setRoute callers) ──

    // Store current route in state (both _system and appData._system for flow compatibility)
    this.stateManager.setState("_system.navigation.currentRoute", this._currentRoute)
    this.stateManager.setState("appData._system.navigation.currentRoute", this._currentRoute)

    // Extract route params
    const routeParams = extractRouteParams(page.route, this._currentRoute)
    this.stateManager.setState("_system.navigation.params", routeParams)

    // Store page name for breadcrumbs
    const pageMeta = page as unknown as Record<string, unknown>
    const resolvedPageName = pageMeta.name ?? pageMeta.title ?? page.id
    this.stateManager.setState("_system.navigation.pageName", resolvedPageName)
    // Also set appData path used by ui.json breadcrumb expression #{appData._system.navigation.pageName}
    this.stateManager.setState("appData._system.navigation.pageName", resolvedPageName)

    // Determine active blocks for this page
    this.refreshActiveBlocks()

    // Auto-sync navigation state from route (runs before init flows so ActionFlow can override)
    this.syncNavigation()

    // Re-apply active page block variable defaults (skip globalState keys).
    // Only re-apply on route CHANGE — when called from addBlocks() (isRouteChange=false),
    // skip the default reset entirely to preserve state populated by prior init flows.
    // This fixes the bug where addBlocks() -> runInitFlows() would reset merchants=[]
    // before the init flow could re-populate the data.
    if (options?.isRouteChange !== false) {
      const globalStateKeys = new Set(Object.keys(this.manifest.globalState ?? {}))
      const currentPage = this.getCurrentPage()
      const layoutBlockName = currentPage?.layout
      for (const blockName of this.activeBlocks) {
        // Skip layout block — its state persists across route changes
        if (layoutBlockName && (blockName === layoutBlockName || camelToKebab(blockName) === layoutBlockName || kebabToCamel(blockName) === layoutBlockName)) continue
        const block = this.blocks[blockName]
        if (!block) continue
        const blockState = block.state as Record<string, unknown>
        const stateEntries = (blockState?.state ?? {}) as Record<string, unknown>
        for (const [key, varDef] of Object.entries(stateEntries)) {
          if (globalStateKeys.has(key) && this.stateManager.get(key) != null) continue
          const resolved = resolveVarDefault(varDef)
          if (resolved !== undefined) {
            const existing = this.stateManager.get(key)
            if (existing != null && typeof existing === "object" && !Array.isArray(existing) &&
                resolved != null && typeof resolved === "object" && !Array.isArray(resolved)) {
              // Merge objects but preserve appData sub-keys that were modified at runtime
              // (e.g., appData.cart.items set by addToCart action should not be overwritten)
              if (key === "appData") {
                const merged = { ...(resolved as Record<string, unknown>) }
                const existingObj = existing as Record<string, unknown>
                for (const [subKey, subVal] of Object.entries(existingObj)) {
                  // Keep runtime-modified sub-keys (non-default values)
                  if (subVal != null) merged[subKey] = subVal
                }
                this.stateManager.setState(key, merged)
              } else {
                this.stateManager.setState(key, { ...(existing as Record<string, unknown>), ...(resolved as Record<string, unknown>) })
              }
            } else {
              this.stateManager.setState(key, resolved)
            }
          }
        }
      }
    }

    // Re-register all flows (ensures callFlow works after route change)
    this.registerAllFlows()

    // ── Load REST datasources before init flows — data must be available for flows ──
    const restPromises: Promise<void>[] = [this.loadRestDataSources()]
    for (const blockName of this.activeBlocks) {
      const block = this.blocks[blockName]
      if (block) restPromises.push(this.loadBlockRestDataSources(blockName, block))
    }

    // ── Init flow execution — delegate to lifecycle for generation counter + initTracker ──
    const context: Record<string, unknown> = { route: { params: routeParams, path: this._currentRoute } }
    return Promise.all(restPromises).then(() =>
      this.lifecycle.activatePage(this.activeBlocks, page.id, context),
    )
  }

  /**
   * Add new blocks incrementally. Existing blocks are skipped, runtime is NOT recreated.
   * Returns the list of newly added block names.
   */
  addBlocks(blocks: Record<string, BlockDef>): string[] {
    applyNavLayoutNormalization(blocks)
    const added = this.lifecycle.addBlocks(blocks)
    if (added.length > 0) {
      this.refreshActiveBlocks()
      // If any newly added block belongs to the current page, re-run init flows
      // so their data loads (fixes: login→dashboard shows no data until refresh).
      const page = this.getCurrentPage()
      if (page) {
        const pageBlocks = new Set([...page.blocks, ...(this.manifest.globalBlocks ?? [])])
        const hasNewPageBlock = added.some(name =>
          pageBlocks.has(name) || pageBlocks.has(kebabToCamel(name)) || pageBlocks.has(camelToKebab(name))
        )
        if (hasNewPageBlock) {
          this.runInitFlows({ isRouteChange: false })
        }
      }
    }
    return added
  }

  /** @deprecated Use addBlocks() instead — kept for backward compatibility */
  updateBlocks(blocks: Record<string, BlockDef>): void {
    this.lifecycle.addBlocks(blocks)
  }

  /**
   * Increment generation counter — invalidates stale async init flow results.
   * Call on mount (to get current gen) and on unmount (to invalidate).
   */
  nextGeneration(): number {
    return this.lifecycle.nextGeneration()
  }

  destroy(): void {
    // Clean up realtime subscriptions (RT-02)
    try {
      const registry = this.stateManager.get('_subscriptionRegistry')
      if (registry && typeof (registry as Record<string, unknown>).unsubscribeAll === 'function') {
        ;(registry as { unsubscribeAll: () => Promise<void> }).unsubscribeAll().catch((err: unknown) =>
          console.warn('[AppRuntime] realtime cleanup failed:', err),
        )
      }
    } catch { /* ignore cleanup errors */ }

    // Clean up auth state change subscription (Phase 73)
    try {
      const authSub = this.stateManager.get('_subscriptions.authStateChange')
      if (authSub && typeof (authSub as Record<string, unknown>).unsubscribe === 'function') {
        ;(authSub as { unsubscribe: () => void }).unsubscribe()
      }
    } catch { /* ignore cleanup errors */ }

    this.unsubState?.()
    this.listeners.clear()
    this.lifecycle.destroy()
    this.webmcp?.dispose()
    this.webmcp = null
  }

  // ─── WebMCP ─────────────────────────────────────────────────────

  /**
   * Install WebMCP polyfill (window.__cogo_tools__) and register core tools.
   * Tools exposed: navigate_to, execute_flow, get_state, set_state.
   * These let the CogoAgent chat control the app via natural language.
   */
  private installWebMCP(): void {
    if (typeof window === "undefined") return
    // Dispose previous instance (idempotent for StrictMode re-mount)
    this.webmcp?.dispose()

    const integration = createWebMCPIntegration({ installPolyfill: true })
    this.webmcp = integration

    // Connect runtime → polyfill (state, events, flows)
    integration.onRuntimeReady({
      stateManager: this.stateManager,
      actionEngine: this.actionEngine,
      eventDispatcher: { dispatch: (id: string, type: string, payload: unknown) => this.dispatchEvent(id, type, payload) },
    })

    const reg = integration.registry

    // ── navigate_to ──
    // Build route→nav params lookup from layout block event bindings.
    // When Agent calls navigate_to, we execute the same navigateTo flow
    // that sidebar menu clicks use — this keeps activeSection, activeNavItem,
    // sectionLabel, and breadcrumbs in sync with the sidebar UI.
    const pages = this.manifest.pages ?? []
    const routes = pages.map(p => p.route)
    const routeDesc = pages.map(p => `"${p.route}" — ${p.title ?? p.id}`).join(", ")

    const routeToNavParams = new Map<string, Record<string, unknown>>()
    for (const [blockName, block] of Object.entries(this.blocks)) {
      const bindings = (block.events as Record<string, unknown> | undefined)?.bindings as Record<string, { flow?: string; params?: Record<string, unknown> }> | undefined
      if (!bindings) continue
      for (const binding of Object.values(bindings)) {
        if (binding.flow === "navigateTo" && binding.params?.route) {
          routeToNavParams.set(binding.params.route as string, binding.params)
        }
      }
    }

    reg.register({
      name: "navigate_to",
      description: `Navigate to a page. Routes: ${routeDesc}`,
      inputSchema: { type: "object", properties: { route: { type: "string", enum: routes } }, required: ["route"] },
      handler: async (params) => {
        const route = params.route as string
        try {
          const timeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("page_load_timeout")), 5000)
          )

          // Navigate first (this triggers runInitFlows + syncNavigation)
          const result = await Promise.race([this.setRoute(route), timeoutPromise])

          // Wait for onNavigate to finish loading blocks (e.g. main.tsx loadBlocks)
          // so new page content is available before the agent's next action.
          await Promise.race([result.navigationReady, timeoutPromise])

          // After navigation, apply sidebar state from event bindings.
          // Re-scan blocks for nav params since blocks may have been added after installWebMCP.
          const freshNavParams = new Map<string, Record<string, unknown>>()
          for (const [, block] of Object.entries(this.blocks)) {
            const bindings = (block.events as Record<string, unknown> | undefined)?.bindings as Record<string, { flow?: string; params?: Record<string, unknown> }> | undefined
            if (!bindings) continue
            for (const binding of Object.values(bindings)) {
              if (binding.flow === "navigateTo" && binding.params?.route) {
                freshNavParams.set(binding.params.route as string, binding.params)
              }
            }
          }
          const navParams = freshNavParams.get(route)
          console.log(`[navigate_to] route="${route}" freshNavParams.size=${freshNavParams.size} match=${!!navParams}`, navParams)
          if (navParams) {
            if (navParams.section) this.stateManager.setState("activeSection", navParams.section as string)
            if (navParams.label) this.stateManager.setState("activeNavItem", navParams.label as string)
            if (navParams.sectionLabel) this.stateManager.setState("sectionLabel", navParams.sectionLabel as string)
            this.notifyListeners()
          }

          return { ok: true, route, loadedBlocks: result.loadedBlocks }
        } catch (err) {
          if (err instanceof Error && err.message === "page_load_timeout") {
            return { ok: false, error: "page_load_timeout" }
          }
          throw err
        }
      },
    })

    // ── execute_flow ──
    const flowNames = this.actionEngine.getRegisteredFlowNames()
    reg.register({
      name: "execute_flow",
      description: `Execute a named ActionFlow. Available: ${flowNames.slice(0, 20).join(", ")}${flowNames.length > 20 ? "..." : ""}`,
      inputSchema: {
        type: "object",
        properties: {
          flowName: { type: "string", description: "Flow name to execute" },
          params: { type: "object", description: "Parameters to pass to the flow" },
        },
        required: ["flowName"],
      },
      handler: async (params) => {
        const flow = this.actionEngine.findFlow(params.flowName as string)
        if (!flow) throw new Error(`Flow "${params.flowName}" not found`)

        const stateChanges: string[] = []
        const originalSetState = this.stateManager.setState.bind(this.stateManager)
        this.stateManager.setState = (path: string, value: unknown) => {
          stateChanges.push(path)
          originalSetState(path, value)
        }

        try {
          await this.actionEngine.executeFlow(flow, (params.params as Record<string, unknown>) ?? {})
          return {
            ok: true,
            result: {
              flowName: params.flowName,
              stateChanges: [...new Set(stateChanges)],
              lastAction: (flow as any).steps?.[(flow as any).steps?.length - 1]?.action ?? null,
            },
          }
        } catch (err) {
          const formatted = formatToolError(err, "execute_flow")
          return {
            ok: false,
            error: { message: formatted.message, code: formatted.classification, hint: "Check flow name and parameters" },
          }
        } finally {
          this.stateManager.setState = originalSetState
        }
      },
    })

    // ── get_state ──
    reg.register({
      name: "get_state",
      description: "Read app state at a given path (e.g. 'appData.kpiData' or 'overviewKpi.revenue')",
      inputSchema: { type: "object", properties: { path: { type: "string" } }, required: ["path"] },
      handler: async (params) => {
        const val = this.stateManager.get(params.path as string)
        return val
      },
      metadata: { readOnly: true },
    })

    // ── set_state ──
    reg.register({
      name: "set_state",
      description: "Write a value to app state at a given path",
      inputSchema: {
        type: "object",
        properties: {
          path: { type: "string" },
          value: { type: "string", description: "JSON-encoded value or plain string" },
        },
        required: ["path", "value"],
      },
      handler: async (params) => {
        let val: unknown = params.value
        try { val = JSON.parse(params.value as string) } catch { /* use as-is */ }
        this.stateManager.setState(params.path as string, val)
        return { ok: true, path: params.path }
      },
    })

    // ── get_available_flows ──
    // NOTE (D-14): This handler calls getRegisteredFlowNames() lazily at each invocation.
    // When pages change and blocks reload with new flows, the next call automatically
    // reflects the updated flow list. No onPageChange wiring needed.
    reg.register({
      name: "get_available_flows",
      description: "List all executable flows with their step counts and action types",
      inputSchema: { type: "object", properties: {} },
      handler: async () => {
        const flowNames = this.actionEngine.getRegisteredFlowNames()
        return flowNames.map(name => {
          const flow = this.actionEngine.findFlow(name)
          const steps = (flow as any)?.steps ?? []
          return {
            name,
            stepCount: steps.length,
            actions: steps.map((s: any) => s.action).filter(Boolean),
          }
        })
      },
      metadata: { readOnly: true },
    })

    // ── dispatch_event ──
    reg.register({
      name: "dispatch_event",
      description: "Dispatch a UI event (e.g. tap) on an element by ID",
      inputSchema: {
        type: "object",
        properties: {
          elementId: { type: "string" },
          eventType: { type: "string", description: "Event type: tap, change, etc." },
        },
        required: ["elementId", "eventType"],
      },
      handler: async (params) => {
        this.dispatchEvent(params.elementId as string, params.eventType as string)
        return { ok: true }
      },
    })

    // ── DSL-generated tools ──
    const blockMap: Record<string, any> = {}
    const flowMap: Record<string, Record<string, any>> = {}
    for (const page of this.manifest.pages ?? []) {
      for (const blockName of page.blocks) {
        const block = this.blocks[blockName]
        if (block?.ui) blockMap[blockName] = block.ui
        if (block?.flow) {
          const raw = block.flow as Record<string, unknown>
          flowMap[blockName] = (raw?.flows as Record<string, any>) ?? raw ?? {}
        }
      }
    }
    try {
      const dslTools = generateToolsFromApp(this.manifest as any, blockMap, flowMap)
      for (const tool of dslTools) {
        // Skip if already registered as core tool
        if (reg.getTool(tool.name)) continue
        // Bind runtime to curried handler
        const originalHandler = tool.handler
        reg.register({
          ...tool,
          handler: async (params) => {
            const runtimeRef = {
              stateManager: this.stateManager,
              actionEngine: this.actionEngine,
            }
            const result = originalHandler(params)
            if (typeof result === "function") return (result as Function)(runtimeRef)
            return result
          },
        })
      }
    } catch (e) {
      console.warn("[WebMCP] DSL tool generation failed:", e)
    }

    // ── Block-level meta.webmcp tools ──
    const activeBlockNames = new Set<string>()
    for (const page of this.manifest.pages ?? []) {
      for (const bn of page.blocks) activeBlockNames.add(bn)
    }
    for (const blockName of activeBlockNames) {
      const block = this.blocks[blockName]
      const webmcpConfig = block?.meta?.webmcp
      if (!webmcpConfig?.expose) continue
      for (const [toolName, toolDef] of Object.entries(webmcpConfig.tools ?? {})) {
        if (reg.getTool(toolName)) continue
        const flowName = toolDef.flow ?? toolName
        reg.register({
          name: toolName,
          description: toolDef.description,
          // toolDef.inputSchema matches JsonSchema structurally but its `properties` field
          // is typed as Record<string, unknown> in the source — bridge through unknown
          // since the fallback we provide is already a valid JsonSchema.
          inputSchema: (toolDef.inputSchema ?? { type: "object", properties: {} }) as unknown as JsonSchema,
          handler: async (params) => {
            const flow = this.actionEngine.findFlow(`${blockName}.${flowName}`)
              ?? this.actionEngine.findFlow(flowName)
            if (flow) return this.actionEngine.executeFlow(flow, { event: params })
            return { error: `Flow ${flowName} not found` }
          },
          metadata: { blockName, readOnly: toolDef.readOnly ?? false, tags: toolDef.tags ?? [] },
        })
      }
    }

    // ── Page context tools ──
    const pageContextRuntime = {
      getCurrentPage: () => {
        const page = this.getCurrentPage()
        if (!page) return null
        return { id: page.id, route: page.route, title: page.title, blocks: page.blocks }
      },
      blocks: this.blocks as Record<string, { ui?: { root?: any } }>,
      getAuthState: () => {
        try {
          const isAuth = this.stateManager.get('appData.auth.isAuthenticated')
          const user = this.stateManager.get('appData.auth.user') as Record<string, unknown> | undefined
          return {
            isAuthenticated: !!isAuth,
            userName: user?.name as string | undefined,
            userType: user?.userType as string | undefined,
          }
        } catch {
          return { isAuthenticated: false }
        }
      },
    }
    const contextTools = [
      ...generatePageContextTools(pageContextRuntime),
      ...generateVisibleElementsTools(pageContextRuntime),
    ]
    for (const tool of contextTools) {
      reg.register(tool)
    }

    console.log(`[WebMCP] Installed ${reg.getManifest().length} tools for "${this.manifest.name}"`)
  }

  // ─── Private methods ─────────────────────────────────────────────

  /**
   * Recompute activeBlocks from current page + globalBlocks + layout block.
   * Called from addBlocks(), runInitFlows(), and setRoute() to ensure
   * event bindings can always find their owning block.
   */
  private refreshActiveBlocks(): void {
    const page = this.getCurrentPage()
    if (!page) return
    this.activeBlocks.clear()
    const addBoth = (name: string) => {
      this.activeBlocks.add(name)
      this.activeBlocks.add(kebabToCamel(name))
      this.activeBlocks.add(camelToKebab(name))
    }
    for (const name of (this.manifest.globalBlocks ?? [])) addBoth(name)
    for (const name of page.blocks) addBoth(name)
    // Include layout block so its event bindings are also discoverable
    if (page.layout && page.layout !== "stack" && page.layout !== "sidebar") {
      addBoth(page.layout)
    }
  }

  /**
   * Auto-sync navigation state from current route.
   * Only activates when layout block's navigation has `sync: "auto"`.
   * Runs before init flows so ActionFlow can override if needed.
   */
  private syncNavigation(): void {
    const page = this.getCurrentPage()
    if (!page?.layout) { console.log(`[syncNav] no layout for page`); return }
    // Pages with layout === "sidebar" | "stack" use buildSidebarPageTree /
    // stack-renderer paths that manage navigation state themselves; the shared
    // navData auto-sync below doesn't apply, so skip silently instead of
    // warning. Matches the same layout filter used at ~line 1182 when
    // building the blocks-lookup map.
    if (page.layout === "sidebar" || page.layout === "stack") return
    const layoutBlock = this.blocks[page.layout]
    if (!layoutBlock) { console.log(`[syncNav] layout block "${page.layout}" not in blocks (keys: ${Object.keys(this.blocks).join(",")})`); return }
    const navData = (layoutBlock.data as Record<string, unknown> | undefined)?.navigation as NavigationData | undefined
    if (!navData || navData.sync !== "auto") { console.log(`[syncNav] no navData or sync!==auto`, !!navData, navData?.sync); return }

    const result = resolveNavFromRoute(navData, this._currentRoute, navData.activeMatch ?? "prefix")
    if (!result) { console.log(`[syncNav] resolveNavFromRoute returned null for route="${this._currentRoute}"`); return }

    console.log(`[syncNav] route="${this._currentRoute}" → tab=${result.tabSlug} item=${result.pageTitle} section=${result.tabTitle}`)
    const map = navData.stateMap ?? {}
    if (map.activeTab) {
      this.stateManager.setState(map.activeTab, result.tabSlug)
      // Also sync appData path used by sidebar group visibility expressions
      // #{appData._system.navigation.activeTopTab === 'financials'} etc.
      this.stateManager.setState("appData._system.navigation.activeTopTab", result.tabSlug)
    }
    if (map.activeItem) {
      this.stateManager.setState(map.activeItem, result.pageTitle)
      this.stateManager.setState(`appData.${map.activeItem}`, result.pageTitle)
    }
    if (map.sectionLabel) {
      this.stateManager.setState(map.sectionLabel, result.tabTitle)
      this.stateManager.setState(`appData.${map.sectionLabel}`, result.tabTitle)
    }
    if (map.breadcrumb) this.stateManager.setState(map.breadcrumb, result.breadcrumb)
  }

  private async setRoute(route: string): Promise<{ loadedBlocks: string[]; navigationReady: Promise<void> }> {
    this._currentRoute = route
    // Fire onNavigate — may return a Promise if it loads blocks asynchronously.
    // We don't await here so that route state sync (syncNavigation) happens immediately.
    const navPromise = Promise.resolve(this.options.onNavigate?.(route)).catch((err) => {
      console.warn(`[AppRuntime] onNavigate("${route}") failed:`, err)
    })
    this.options.platform?.pushRoute(route)
    this.lifecycle.resetForRoute()  // Allow init flows to re-run for the new page
    await this.runInitFlows()
    this.notifyListeners()
    const page = this.getCurrentPage()
    return { loadedBlocks: page?.blocks ?? [], navigationReady: navPromise }
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) listener()
  }

  /**
   * Initialize manifest-level state (appData, globalState, datasources, theme).
   * Block-level state is initialized per-block via initSingleBlockState().
   */
  private initializeManifestState(): void {
    const sm = this.stateManager

    // Standard namespace aliases
    sm.registerNamespaceAlias("ui")
    sm.registerNamespaceAlias("state")
    sm.registerNamespaceAlias("computed")

    // appData
    if (this.manifest.appData) {
      for (const [key, value] of Object.entries(this.manifest.appData)) {
        sm.setState(`appData.${key}`, structuredClone(value))
      }
    }

    // globalState defaults
    if (this.manifest.globalState) {
      for (const [key, def] of Object.entries(this.manifest.globalState)) {
        const varDef = def as unknown as Record<string, unknown> // TS2352 bridge — VariableDef shape varies; shallow default lookup only
        if (varDef.default !== undefined) {
          sm.setState(key, structuredClone(varDef.default))
        }
      }
    }

    // datasources — static data loaded immediately, REST fallback seeds loaded here.
    // Seed priority: `ds.data` (legacy) → `ds.response.default` (formal contract).
    for (const [name, ds] of Object.entries(this.manifest.datasources ?? {})) {
      if (ds.type !== "static" && ds.type !== "rest" && ds.type !== "restApi") continue
      const seed = ds.data !== undefined
        ? ds.data
        : ds.response?.default !== undefined
          ? ds.response.default
          : undefined
      if (seed !== undefined) sm.setState(name, structuredClone(seed))
    }

    // Theme CSS variables — use var(--color-*) which resolves correctly
    // for both oklch and legacy HSL formats (set by getIsolatedAppStyle)
    const themeVarMap: Record<string, string> = {
      background: "var(--color-background)",
      foreground: "var(--color-foreground)",
      card: "var(--color-card)",
      cardForeground: "var(--color-card-foreground)",
      popover: "var(--color-popover)",
      popoverForeground: "var(--color-popover-foreground)",
      primary: "var(--color-primary)",
      primaryForeground: "var(--color-primary-foreground)",
      secondary: "var(--color-secondary)",
      secondaryForeground: "var(--color-secondary-foreground)",
      muted: "var(--color-muted)",
      mutedForeground: "var(--color-muted-foreground)",
      accent: "var(--color-accent)",
      accentForeground: "var(--color-accent-foreground)",
      destructive: "var(--color-destructive)",
      destructiveForeground: "var(--color-destructive-foreground)",
      border: "var(--color-border)",
      input: "var(--color-input)",
      ring: "var(--color-ring)",
      sidebar: "var(--color-sidebar)",
      sidebarForeground: "var(--color-sidebar-foreground)",
      success: "oklch(0.545 0.175 142.5)",
      warning: "oklch(0.75 0.183 55.934)",
    }
    for (const [key, val] of Object.entries(themeVarMap)) {
      sm.setState(`theme.${key}`, val)
    }

    // Design system tokens → ds.* namespace
    if (Object.keys(this.designSystem).length > 0) {
      for (const [tokenName, className] of Object.entries(this.designSystem)) {
        sm.setState(`ds.${tokenName}`, className)
      }
      sm.registerNamespaceAlias("ds")
    }

    // Theme tokens are applied via inline style on AppV2's root div (tokenStyle).
    // Do NOT inject into document.documentElement — that pollutes the host app
    // (e.g., Studio IDE buttons turn purple when a Bakery template is previewed).
  }

  /**
   * Load REST datasources from manifest.datasources.
   * Fetches each rest-type datasource endpoint and sets the result in state.
   * On failure: sets `{name}_error` state and falls back to default data (if any).
   * Called once before init flows in runInitFlows().
   */
  private restDataSourcesLoaded = false
  private async loadRestDataSources(): Promise<void> {
    if (this.restDataSourcesLoaded) return
    this.restDataSourcesLoaded = true

    const entries = Object.entries(this.manifest.datasources ?? {}).filter(
      ([, ds]) => ds.type === "rest" && ds.endpoint,
    )
    if (entries.length === 0) return

    const sm = this.stateManager
    await Promise.all(
      entries.map(async ([name, ds]) => {
        try {
          const resp = await fetch(ds.endpoint!, {
            method: ds.method ?? "GET",
            headers: { ...ds.headers, ...this.getMerchantHeaders() },
          })
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
          const data = await resp.json()
          const mapped = ds.responseMapping
            ? applyResponseMapping(data, ds.responseMapping)
            : data
          sm.setState(name, mapped)
        } catch (err) {
          console.warn(`[AppRuntime] REST datasource "${name}" failed:`, err)
          sm.setState(`${name}_error`, String(err))
          // Fallback: only set `response.default` when no legacy `ds.data`
          // was seeded — otherwise preserve the legacy seed already in state.
          if (ds.data == null) {
            const fallback = ds.response?.default
            if (fallback !== undefined) sm.setState(name, structuredClone(fallback))
          }
        }
      }),
    )
  }

  /**
   * Load REST datasources from a block's data.json.
   * Called lazily before init flows for blocks with rest-type sources.
   */
  private readonly blockRestLoaded = new Set<string>()
  private async loadBlockRestDataSources(blockName: string, block: BlockDef): Promise<void> {
    if (this.blockRestLoaded.has(blockName)) return
    this.blockRestLoaded.add(blockName)

    const dataSources = block.data as Record<string, unknown>
    if (!dataSources || typeof dataSources !== "object") return

    const sources = (dataSources.sources as Record<string, unknown>) ?? dataSources
    const sm = this.stateManager

    const restEntries = Object.entries(sources).filter(([, ds]) => {
      if (!ds || typeof ds !== "object") return false
      const src = ds as Record<string, unknown>
      return src.type === "rest" && src.endpoint
    })
    if (restEntries.length === 0) return

    await Promise.all(
      restEntries.map(async ([name, ds]) => {
        const src = ds as DataSource
        try {
          const resp = await fetch(src.endpoint!, {
            method: src.method ?? "GET",
            headers: { ...src.headers, ...this.getMerchantHeaders() },
          })
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
          const data = await resp.json()
          const mapped = src.responseMapping
            ? applyResponseMapping(data, src.responseMapping)
            : data
          sm.setState(name, mapped)
        } catch (err) {
          console.warn(`[AppRuntime] Block "${blockName}" REST datasource "${name}" failed:`, err)
          sm.setState(`${name}_error`, String(err))
          // Fall back to formal `response.default` when no legacy `src.data`
          // exists — `src.data` is already the current state, preserving it
          // matches the legacy behavior. `response.default` fills the gap for
          // sources that use the formal contract only.
          if (src.data == null) {
            const fallback = src.response?.default
            if (fallback !== undefined) sm.setState(name, structuredClone(fallback))
          }
        }
      }),
    )
  }

  /**
   * Initialize state for a single block — called by BlockLifecycleManager.addBlocks().
   * Handles: namespace aliases, state.json, localState, props, computed, datasources, NavigationData.
   */
  private initSingleBlockState(blockName: string, block: BlockDef): void {
    const sm = this.stateManager

    // Namespace aliases
    const camelName = kebabToCamel(blockName)
    sm.registerNamespaceAlias(camelName)
    if (camelName !== blockName) sm.registerNamespaceAlias(blockName)

    const blockState = block.state as Record<string, unknown>

    // state.json → flat keys
    const stateEntries = (blockState?.state ?? {}) as Record<string, unknown>
    for (const [key, varDef] of Object.entries(stateEntries)) {
      const resolved = resolveVarDefault(varDef)
      if (resolved !== undefined) {
        // Register declaration first so lazy-init can recover the default
        // even after setState(path, undefined) is called.
        sm.registerDeclaration?.(key, { default: resolved })
        const existing = sm.get(key)
        if (existing != null && typeof existing === "object" && !Array.isArray(existing) &&
            resolved != null && typeof resolved === "object" && !Array.isArray(resolved)) {
          // Object: merge (preserve existing keys)
          sm.setState(key, { ...(existing as Record<string, unknown>), ...(resolved as Record<string, unknown>) })
        } else {
          sm.setState(key, resolved)
        }
      }
    }

    // localState → "local.{key}"
    const localState = (blockState as Record<string, unknown>)?.localState
    if (localState && typeof localState === "object") {
      for (const [key, varDef] of Object.entries(localState as Record<string, unknown>)) {
        const resolved = resolveVarDefault(varDef)
        if (resolved !== undefined) {
          sm.registerDeclaration?.(`local.${key}`, { default: resolved })
          sm.setState(`local.${key}`, resolved)
        }
      }
    }

    // props → "props.{key}"
    const propsVars = (blockState as Record<string, unknown>)?.props
    if (propsVars && typeof propsVars === "object") {
      for (const [key, varDef] of Object.entries(propsVars as Record<string, unknown>)) {
        const resolved = resolveVarDefault(varDef)
        if (resolved !== undefined) {
          sm.registerDeclaration?.(`props.${key}`, { default: resolved })
          sm.setState(`props.${key}`, resolved)
        }
      }
    }

    // computed → register expressions
    const computed = (blockState?.computed ?? {}) as Record<string, string | { expression?: string }>
    for (const [key, val] of Object.entries(computed)) {
      const expr = typeof val === "string"
        ? val
        : (typeof val === "object" && val !== null && "expression" in val)
          ? (val as { expression?: string }).expression
          : null
      if (typeof expr === "string") {
        this.computedExprs.set(key, expr)
        const resolved = sm.resolve(expr)
        sm.setState(`computed.${key}`, resolved)
      }
    }

    // datasources → static data + REST/restApi pre-fetch seed
    // Seed priority: `src.data` (legacy free-form) → `src.response.default` (formal contract).
    // Applies to static (one-shot seed), rest (pre-fetch + fallback), and restApi
    // (flow-invoked; seed still useful so compute reads don't hit undefined
    // before the first restApi action fires).
    const dataSources = block.data as Record<string, unknown>
    if (dataSources && typeof dataSources === "object") {
      const sources = (dataSources.sources as Record<string, unknown>) ?? dataSources
      for (const [name, ds] of Object.entries(sources)) {
        if (!ds || typeof ds !== "object") continue
        const src = ds as Record<string, unknown>
        const type = src.type
        if (type !== "static" && type !== "rest" && type !== "restApi") continue
        const response = src.response as { default?: unknown } | undefined
        const seed = src.data != null
          ? src.data
          : response && response.default !== undefined
            ? response.default
            : undefined
        if (seed !== undefined) {
          sm.registerDeclaration?.(name, { default: structuredClone(seed) })
          sm.setState(name, structuredClone(seed))
        }
      }

      // Register NavigationData from block data as #{nav.*} state.
      const navRaw = dataSources.navigation as Record<string, unknown> | undefined
      if (navRaw) {
        sm.setState("nav", {
          pages: navRaw.pages ?? [],
          brand: navRaw.brand ?? null,
          footer: navRaw.footer ?? [],
          tabs: navRaw.tabs ?? [],
        })
        sm.registerNamespaceAlias("nav")
      }
    }
  }

  /**
   * Register flows from a single block — called by BlockLifecycleManager.addBlocks().
   */
  private registerSingleBlockFlows(blockName: string, block: BlockDef): void {
    const rawFlows = block.flow as Record<string, unknown>
    const flowEntries = (rawFlows?.flows as Record<string, unknown>) ?? rawFlows ?? {}
    const ns = camelToKebab(block.meta?.namespace ?? blockName)
    const flows: FlowMap = {}
    for (const [key, flow] of Object.entries(flowEntries)) {
      if (typeof flow !== "object" || flow === null) continue
      const fullKey = key.includes(".") ? key : `${ns}.${key}`
      flows[fullKey] = flow as Flow
    }
    if (Object.keys(flows).length > 0) {
      this.actionEngine.registerFlows(flows)
    }
  }

  registerAllFlows(): void {
    const allFlows: FlowMap = {}
    for (const [blockName, block] of Object.entries(this.blocks)) {
      const rawFlows = block.flow as Record<string, unknown>
      const flowEntries = (rawFlows?.flows as Record<string, unknown>) ?? rawFlows ?? {}
      const ns = camelToKebab(block.meta?.namespace ?? blockName)
      for (const [key, flow] of Object.entries(flowEntries)) {
        if (typeof flow !== "object" || flow === null) continue
        const fullKey = key.includes(".") ? key : `${ns}.${key}`
        allFlows[fullKey] = flow as Flow
      }
    }
    this.actionEngine.registerFlows(allFlows)
    console.log(`[AppRuntime] Registered ${Object.keys(allFlows).length} flows from ${Object.keys(this.blocks).length} blocks`)
  }

  private registerBlockBindings(block: BlockDef, blockNs: string): void {
    const ed = this.eventDispatcher

    // Inline UI events
    walkElements(block.ui?.root, (el) => {
      if (el.events) {
        for (const [eventType, handler] of Object.entries(el.events)) {
          if (typeof handler === "string") {
            ed.addBinding(el.id, eventType, { flow: handler, __ns: blockNs })
          } else if (typeof handler === "object" && handler !== null) {
            const h = handler as EventRef
            if (h.action === "navigate") {
              ed.addBinding(el.id, eventType, { flow: `__navigate:${h.params?.route ?? "/"}`, __ns: blockNs })
            } else if (h.flow) {
              ed.addBinding(el.id, eventType, { flow: h.flow, params: h.params, __ns: blockNs })
            }
          }
        }
      }
    })

    // events.json bindings
    const evts = block.events as Record<string, unknown>
    if (!evts) return

    // v1: { bindings: { "elementId:eventType": binding } }
    const bindings = evts.bindings as Record<string, unknown> | undefined
    if (bindings && typeof bindings === "object") {
      for (const [bindingKey, binding] of Object.entries(bindings)) {
        const colonIdx = bindingKey.lastIndexOf(":")
        if (colonIdx > 0) {
          const elementId = bindingKey.slice(0, colonIdx)
          const eventType = bindingKey.slice(colonIdx + 1)
          if (typeof binding === "string") {
            ed.addBinding(elementId, eventType, { flow: binding, __ns: blockNs })
          } else if (typeof binding === "object" && binding !== null) {
            const b = binding as { flow?: string; action?: string; params?: Record<string, unknown> }
            if (b.flow) {
              ed.addBinding(elementId, eventType, { flow: b.flow, params: b.params, __ns: blockNs })
            } else if (b.action === "navigate") {
              const params = b.params as Record<string, string> | undefined
              ed.addBinding(elementId, eventType, { flow: `__navigate:${params?.route ?? "/"}`, __ns: blockNs })
            }
          }
        }
      }
    }

    // v2: { elementId: { eventType: binding } }
    for (const [elementId, eventMap] of Object.entries(evts)) {
      if (elementId === "bindings") continue
      if (!eventMap || typeof eventMap !== "object") continue
      for (const [eventType, binding] of Object.entries(eventMap as Record<string, unknown>)) {
        if (typeof binding !== "object" || binding === null) continue
        const b = binding as { flow?: string; action?: string; params?: Record<string, unknown> }
        if (b.flow) {
          ed.addBinding(elementId, eventType, { flow: b.flow, params: b.params, __ns: blockNs })
        } else if (b.action === "navigate") {
          const params = b.params as Record<string, string> | undefined
          ed.addBinding(elementId, eventType, { flow: `__navigate:${params?.route ?? "/"}`, __ns: blockNs })
        }
      }
    }
  }

  private registerBuiltinActions(platformAdapter: PlatformAdapter): void {
    const sm = this.stateManager
    const ae = this.actionEngine

    ae.registerAction("navigate", async (params) => {
      const route = params.route as string
      if (!route) return
      const mode = (params.mode as string) || "push"
      if (mode === "replace") {
        platformAdapter.replaceRoute(route)
      } else {
        platformAdapter.pushRoute(route)
      }
    })

    ae.registerAction("compute", (params) => {
      const target = params.target as string
      if (!target) return undefined
      // value form (object literal already resolved by resolveParams)
      if (params.value !== undefined) {
        sm.setState(target.replace(/^#/, ""), params.value)
        return params.value
      }
      // expression form
      const expression = params.expression as string
      if (expression) {
        const resolved = sm.getCompute().evaluate(expression)
        sm.setState(target.replace(/^#/, ""), resolved)
        return resolved
      }
      return undefined
    })

    ae.registerAction("sortByField", (params) => {
      const source = params.source as string
      const target = params.target as string
      const field = params.field as string
      const direction = (params.direction as string) || "asc"
      if (!source || !target || !field) return
      const arr = sm.get(source.replace(/^#/, ""))
      if (!Array.isArray(arr)) return
      const sorted = [...arr].sort((a, b) => {
        const av = a?.[field]
        const bv = b?.[field]
        if (av == null && bv == null) return 0
        if (av == null) return 1
        if (bv == null) return -1
        if (typeof av === "number" && typeof bv === "number") {
          return direction === "asc" ? av - bv : bv - av
        }
        const sa = String(av).toLowerCase()
        const sb = String(bv).toLowerCase()
        if (sa < sb) return direction === "asc" ? -1 : 1
        if (sa > sb) return direction === "asc" ? 1 : -1
        return 0
      })
      sm.setState(target.replace(/^#/, ""), sorted)
    })

    // CSV export: convert array data to CSV and trigger download
    ae.registerAction("csvExport", (params) => {
      const data = params.data as Record<string, unknown>[] | undefined
      if (!Array.isArray(data) || data.length === 0) return
      const columns = params.columns as Array<{ key: string; header: string }> | undefined
      const filename = String(params.filename ?? "export.csv")
      const headers = columns ? columns.map((c) => c.header) : Object.keys(data[0])
      const keys = columns ? columns.map((c) => c.key) : Object.keys(data[0])
      const esc = (val: unknown): string => {
        const s = val == null ? "" : String(val)
        return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s
      }
      const csv = [headers.map(esc).join(","), ...data.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\n")
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 5000)
    })

    // JSON export: download state data as JSON file
    ae.registerAction("jsonExport", (params) => {
      const data = params.data
      const filename = String(params.filename ?? "export.json")
      const json = JSON.stringify(data, null, 2)
      const blob = new Blob([json], { type: "application/json" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 5000)
    })
  }

  private findFlowBySuffix(flowName: string): Flow | undefined {
    const names = this.actionEngine.getRegisteredFlowNames()
    const match = names.find((k) => k.endsWith(`.${flowName}`))
    return match ? this.actionEngine.findFlow(match) : undefined
  }

  /** Enrich block with events.json bindings injected into UI tree. */
  private enrichBlock(blockName: string): BlockDef | undefined {
    const block = this.blocks[blockName]
    if (!block) return undefined
    return injectEventsIntoUi(block)
  }
}

// ─── Pure utility functions (no class, no React) ──────────────────────────

const kebabToCamel = (s: string) => s.replace(/-([a-z])/g, (_: string, c: string) => c.toUpperCase())
const camelToKebab = (s: string) => s.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase()

/** Map blockLayout.width semantic values to Tailwind classes. */
const WIDTH_CLASS_MAP: Record<string, string> = {
  constrained: "max-w-[72rem] mx-auto",
  narrow: "max-w-[48rem] mx-auto",
  full: "",
}

/** Detect header/sticky blocks — render without section wrapper. */
function isHeaderBlock(root: UiElement | undefined): boolean {
  if (!root) return false
  if (root.variant === "header" || root.tag === "header") return true
  if (root.position?.type === "sticky") return true
  const opts = (root as unknown as Record<string, unknown>).options as Record<string, unknown> | undefined // TS2352 bridge — optional options bag not in UiElement contract
  if (opts?.position === "sticky") return true
  return false
}

/** Detect footer blocks by name or tag. */
function isFooterBlock(blockName: string, root: UiElement | undefined): boolean {
  if (blockName.includes("footer")) return true
  if (root?.tag === "footer" || root?.variant === "footer") return true
  return false
}

/**
 * Detect stub blocks — blocks whose only content is a [block-name] placeholder text.
 * These are generated when the LLM fails to produce real UI content for a block.
 * Skip them from rendering to avoid showing raw placeholder text to end users.
 */
function isStubBlock(root: UiElement | undefined, blockName: string): boolean {
  if (!root) return true
  const children = root.children
  if (!children || children.length !== 1) return false
  const child = children[0]
  if (child.type !== "text") return false
  const text = (child.text ?? "").trim()
  // Match [block-name] pattern (exact match or similar)
  return text === `[${blockName}]` || /^\[[\w-]+\]$/.test(text)
}

export function matchRoute(pattern: string, path: string): boolean {
  const patternParts = pattern.split("/")
  const pathParts = path.split("/")
  if (patternParts.length !== pathParts.length) return false
  return patternParts.every((part, i) => {
    if (part.startsWith(":")) return true
    return part === pathParts[i]
  })
}

/** Return -1 if pattern does not match path. Otherwise return a specificity score:
 *  each literal segment is worth 1 point, each :param is worth 0. Higher = more specific. */
export function scorePageSpecificity(pattern: string, path: string): number {
  const patternParts = pattern.split("/")
  const pathParts = path.split("/")
  if (patternParts.length !== pathParts.length) return -1
  let score = 0
  for (let i = 0; i < patternParts.length; i++) {
    const part = patternParts[i]
    if (part.startsWith(":")) continue
    if (part !== pathParts[i]) return -1
    score++
  }
  return score
}

function extractRouteParams(pattern: string, path: string): Record<string, string> {
  const params: Record<string, string> = {}
  const patternParts = pattern.split("/")
  const pathParts = path.split("/")
  patternParts.forEach((part, i) => {
    if (part.startsWith(":") && pathParts[i]) {
      params[part.slice(1)] = pathParts[i]
    }
  })
  return params
}

function walkElements(element: UiElement | undefined | null, fn: (el: UiElement) => void): void {
  if (!element) return
  fn(element)
  element.children?.forEach((child) => walkElements(child, fn))
}

function resolveVarDefault(varDef: unknown): unknown {
  if (varDef != null && typeof varDef === "object" && !Array.isArray(varDef)) {
    const def = varDef as Record<string, unknown>
    if (def.default !== undefined) return structuredClone(def.default)
    if (def.type === undefined) return structuredClone(varDef)
    return undefined
  }
  return varDef !== undefined ? structuredClone(varDef) : undefined
}

function resolveBindingParams(
  params: Record<string, unknown>,
  context: Record<string, unknown>,
  stateManager?: { resolve(expr: string): unknown },
): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(params)) {
    if (typeof value === "string") {
      const pureMatch = /^#\{([^}]+)\}$/.exec(value)
      if (pureMatch) {
        const contextVal = evaluateContextExpression(pureMatch[1].trim(), context)
        if (contextVal !== undefined) {
          result[key] = contextVal
        } else if (stateManager) {
          const stateVal = stateManager.resolve(value)
          result[key] = stateVal !== value ? stateVal : undefined
        } else {
          result[key] = undefined
        }
      } else {
        result[key] = value
      }
    } else {
      result[key] = value
    }
  }
  return result
}

function evaluateContextExpression(expr: string, context: Record<string, unknown>): unknown {
  const pathValue = getFromContext(expr, context)
  if (pathValue !== undefined) return pathValue

  let processed = expr
  const refs: Array<{ match: string; value: unknown }> = []
  processed = expr.replace(/([a-zA-Z_][\w]*(?:\.[\w]+)*)/g, (match) => {
    const val = getFromContext(match, context)
    if (val !== undefined) {
      refs.push({ match, value: val })
      if (typeof val === "number") return val.toString()
      if (typeof val === "string") {
        const n = parseFloat(val)
        return isNaN(n) ? JSON.stringify(val) : n.toString()
      }
      return String(val)
    }
    return match
  })

  if (refs.length === 0) return undefined
  if (refs.length === 1 && expr.trim() === refs[0].match) return refs[0].value

  const trimmed = processed.trim()
  const numResult = parseFloat(trimmed)
  if (!isNaN(numResult) && /^-?\d+(\.\d+)?$/.test(trimmed)) return numResult

  const binMatch = /^(-?\d+(?:\.\d+)?)\s*([+\-*/])\s*(-?\d+(?:\.\d+)?)$/.exec(trimmed)
  if (binMatch) {
    const l = parseFloat(binMatch[1])
    const r = parseFloat(binMatch[3])
    switch (binMatch[2]) {
      case "+": return l + r
      case "-": return l - r
      case "*": return l * r
      case "/": return r !== 0 ? l / r : 0
    }
  }

  return refs[0]?.value
}

function getFromContext(path: string, ctx: Record<string, unknown>): unknown {
  const keys = path.split(".")
  let current: unknown = ctx
  for (const key of keys) {
    if (current == null || typeof current !== "object") return undefined
    current = (current as Record<string, unknown>)[key]
  }
  return current
}

/** Find the content slot in a layout UI tree. */
export function findContentSlot(root: UiElement): UiElement | null {
  let found: UiElement | null = null
  walkTree(root, (el) => { if (el.id === "content-body") found = el })
  if (found) return found

  walkTree(root, (el) => { if (!found && el.id?.includes("content")) found = el })
  if (found) return found

  let deepest: UiElement | null = null
  let maxDepth = 0
  function findDeepest(el: UiElement, depth: number) {
    if (el.type === "container" && (!el.children || el.children.length === 0)) {
      if (depth > maxDepth) { maxDepth = depth; deepest = el }
    }
    el.children?.forEach((c) => findDeepest(c, depth + 1))
  }
  findDeepest(root, 0)
  return deepest
}

function walkTree(el: UiElement, fn: (el: UiElement) => void) {
  fn(el)
  el.children?.forEach((c) => walkTree(c, fn))
}

/**
 * Inject events.json bindings into UI tree elements.
 * Renderers check element.events.tap to attach onClick handlers.
 */
function injectEventsIntoUi(block: BlockDef): BlockDef {
  const evts = block.events as Record<string, unknown> | undefined
  if (!evts) return block

  const bindings = (evts.bindings ?? evts) as Record<string, unknown>
  if (!bindings || typeof bindings !== "object") return block

  const eventMap = new Map<string, Record<string, unknown>>()
  for (const [key, binding] of Object.entries(bindings)) {
    const colonIdx = key.lastIndexOf(":")
    if (colonIdx <= 0) continue
    const elementId = key.slice(0, colonIdx)
    const eventType = key.slice(colonIdx + 1)
    if (!eventMap.has(elementId)) eventMap.set(elementId, {})
    eventMap.get(elementId)![eventType] = binding
  }

  if (eventMap.size === 0) return block

  const ui = structuredClone(block.ui)
  function walk(node: Record<string, unknown> | undefined): void {
    if (!node) return
    const id = node.id as string | undefined
    if (id && eventMap.has(id)) {
      const existing = (node.events ?? {}) as Record<string, unknown>
      node.events = { ...existing, ...eventMap.get(id) }
    }
    const children = node.children as Array<Record<string, unknown>> | undefined
    if (Array.isArray(children)) children.forEach(walk)
    const template = node.template as Record<string, unknown> | undefined
    if (template) walk(template)
  }
  walk(ui?.root as unknown as Record<string, unknown>)

  return { ...block, ui } as BlockDef
}

/**
 * Apply a simple dot-path response mapping to extract a nested value from API response.
 * e.g., responseMapping = "data.products" extracts response.data.products
 */
function applyResponseMapping(data: unknown, mapping: string): unknown {
  const keys = mapping.split(".")
  let current: unknown = data
  for (const key of keys) {
    if (current == null || typeof current !== "object") return data
    current = (current as Record<string, unknown>)[key]
  }
  return current ?? data
}
